import type { Agent } from './agent';
import { InvalidToolInputError, ToolTimeoutError, UserError } from './errors';
import type { RunToolApprovalItem, RunToolCallOutputItem } from './items';
import logger from './logger';
import type { RunResult } from './result';
import type { RunConfig } from './run';
import type { RunContext } from './run-context';
import {
  resolveToolInputGuardrails,
  resolveToolOutputGuardrails,
  type ToolInputGuardrailDefinition,
  type ToolInputGuardrailFunction,
  type ToolOutputGuardrailDefinition,
  type ToolOutputGuardrailFunction,
} from './tool-guardrails';
import { getCurrentSpan } from './tracing/context';
import type {
  JsonObjectSchema,
  JsonObjectSchemaNonStrict,
  JsonObjectSchemaStrict,
  UnknownContext,
} from './types';
import * as protocol from './types/protocol';
import * as ProviderData from './types/provider-data';
import { combineAbortSignals } from './utils/abort-signals';
import { safeExecute } from './utils/safe-execute';
import { toSmartString } from './utils/smart-string';
import {
  getSchemaAndParserFromInputType,
  toFunctionToolName,
} from './utils/tools';
import { isZodObject } from './utils/type-guards';
import type { ZodInfer, ZodObjectLike } from './utils/zod-compact';

const MAX_FUNCTION_TOOL_TIMEOUT_MS = 2_147_483_647;
const FUNCTION_TOOL_TIMEOUT_ALREADY_ENFORCED = Symbol(
  'functionToolTimeoutAlreadyEnforced',
);
const FUNCTION_TOOL_TIMEOUT_BEHAVIORS = [
  'error_as_result',
  'raise_exception',
] as const satisfies ReadonlyArray<FunctionToolTimeoutBehavior>;

export type FunctionToolTimeoutBehavior = 'error_as_result' | 'raise_exception';

export type ToolErrorResult<Output = unknown> = {
  is_error: true;
  output: Output;
  display?: unknown;
};

export type ResolvedToolResult = {
  isError: boolean;
  output: unknown;
  /**
   * UI 专用通道：结构化展示数据。不会进入模型上下文，仅随 `function_call_result` 持久化并下发给前端。
   */
  display?: unknown;
};

const TOOL_RESULT_BRAND = Symbol.for('lyra.toolResult');

const MAX_DISPLAY_BYTES = 256 * 1024;

export type ToolDualResult<Output = unknown> = {
  [TOOL_RESULT_BRAND]: true;
  is_error?: true;
  output: Output;
  display?: unknown;
};

/**
 * 包装工具返回值，区分两个通道：`output` 回灌模型（现状语义），`display` 仅供 UI 渲染。
 *
 * `display` 必须可 JSON 序列化（否则在调用现场抛 `UserError`）；序列化后超过 256KB 时
 * 丢弃 display 并记录警告，工具结果本身不受影响。
 */
export function toolResult<Output = unknown>(fields: {
  output: Output;
  display?: unknown;
}): ToolDualResult<Output> {
  return {
    [TOOL_RESULT_BRAND]: true,
    output: fields.output,
    display: normalizeDisplay(fields.display),
  };
}

export function toolError<Output = unknown>(
  output: Output,
  options?: { display?: unknown },
): ToolErrorResult<Output> & ToolDualResult<Output> {
  return {
    [TOOL_RESULT_BRAND]: true,
    is_error: true,
    output,
    display: normalizeDisplay(options?.display),
  };
}

function normalizeDisplay(display: unknown): unknown {
  if (display === undefined) {
    return undefined;
  }

  let json: string;
  try {
    json = JSON.stringify(display);
  } catch (error) {
    throw new UserError(
      `Tool display payload must be JSON-serializable: ${error}`,
    );
  }
  if (json === undefined) {
    throw new UserError(
      'Tool display payload must be JSON-serializable (got a non-serializable value).',
    );
  }
  if (json.length > MAX_DISPLAY_BYTES) {
    logger.warn(
      `Tool display payload dropped: ${json.length} bytes exceeds the ${MAX_DISPLAY_BYTES} byte limit.`,
    );
    return undefined;
  }

  return JSON.parse(json) as unknown;
}

export function resolveToolResult(value: unknown): ResolvedToolResult {
  if (hasOwnKey(value, TOOL_RESULT_BRAND)) {
    const branded = value as ToolDualResult;
    return {
      isError: branded.is_error === true,
      output: branded.output,
      display: branded.display,
    };
  }

  if (!hasOwnKey(value, 'is_error')) {
    return {
      isError: false,
      output: value,
    };
  }

  if (!isPlainObject(value)) {
    throw new UserError('Tool result wrapper must be an object.');
  }

  if (value.is_error !== true) {
    throw new UserError(
      'Tool result wrapper only supports is_error: true. Return plain output for success.',
    );
  }

  if (!hasOwnKey(value, 'output')) {
    throw new UserError(
      'Tool error result wrapper must include an output field.',
    );
  }

  const unexpectedKeys = Reflect.ownKeys(value).filter(
    (key) => key !== 'is_error' && key !== 'output',
  );
  if (unexpectedKeys.length > 0) {
    throw new UserError(
      'Tool error result wrapper only supports is_error and output fields.',
    );
  }

  return {
    isError: true,
    output: value.output,
  };
}

function hasOwnKey(
  value: unknown,
  key: PropertyKey,
): value is Record<PropertyKey, unknown> {
  return (
    typeof value === 'object' && value !== null && Object.hasOwn(value, key)
  );
}

function isPlainObject(value: unknown): value is Record<PropertyKey, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export type ToolTimeoutErrorFunction<Context = UnknownContext> = (
  context: RunContext<Context>,
  error: ToolTimeoutError,
) => Promise<string> | string;

/**
 * 一个用于判断是否应批准某个工具调用的函数。
 *
 * @param runContext 当前的运行上下文
 * @param input 工具的输入参数
 * @param callId 工具调用的 ID
 * @returns 如果应批准该工具调用则返回 true，否则返回 false
 */
export type ToolApprovalFunction<T extends ToolInputParameters> = (
  runContext: RunContext,
  input: ToolExecuteArgument<T>,
  callId?: string,
) => Promise<boolean>;

/**
 * 工具的参数类型。
 *
 * 参数类型由工具定义中传入的参数模式推导而
 * 如果参数以 JSON 模式传入, 则类型为 `unknown`
 * 如果是 Zod 模式, 则类型将匹配推导出的 Zod 类型
 * 否则, 类型为 `string`
 */
export type ToolExecuteArgument<T extends ToolInputParameters> =
  T extends ZodObjectLike
    ? ZodInfer<T>
    : T extends JsonObjectSchema<any>
      ? unknown
      : string;

export type ToolCallDetails = {
  toolCall?: protocol.FunctionCallItem;
  resumeState?: string;
  signal?: AbortSignal;
  /**
   * 内部使用：用于嵌套智能体工具运行（Agent.asTool）的父运行器配置。
   */
  parentRunConfig?: Partial<RunConfig>;
};

/**
 * 工具的参数定义。
 * 可以是 Zod 模式、JSON 模式或 undefined。
 * 如果提供的是 Zod 模式，工具的入参将自动根据该模式进行解析和校验。
 * 如果提供的是 JSON 模式，工具的入参将按原样传入。
 * 如果提供的是 undefined，工具的入参将作为字符串传入。
 */
export type ToolInputParameters =
  | undefined
  | ZodObjectLike
  | JsonObjectSchema<any>;

/**
 * 将一个函数作为工具暴露给智能体，供其调用
 *
 * @param Context 该工具的上下文
 * @param Result 该工具的结果
 */

export type FunctionTool<
  Context = UnknownContext,
  TParameters extends ToolInputParameters = undefined,
  Result = unknown,
> = {
  type: 'function';
  name: string;
  description: string;
  parameters: JsonObjectSchema<any>;
  /**
   * Controls how this tool is scheduled relative to other calls from the same model turn.
   */
  execution?: FunctionToolExecutionOptions;
  /**
   * 工具是否启用严格模式。若为 true，模型必须尽量严格遵循该模式（可能导致响应时间变慢）。
   */
  strict: boolean;

  /**
   * 工具被调用时要执行的函数。
   */
  invoke: (
    runContext: RunContext<Context>,
    input: string,
    details?: ToolCallDetails,
  ) => Promise<string | Result | ToolErrorResult>;

  /**
   * @internal
   * Parses and validates tool input without executing the tool.
   */
  parseInput?: (
    runContext: RunContext<Context>,
    input: string,
    details?: ToolCallDetails,
  ) => Promise<unknown>;

  /**
   * @internal
   * Formats a tool error as a model-visible error result.
   */
  handleError?: (
    runContext: RunContext<Context>,
    error: unknown,
    details?: ToolCallDetails,
  ) => Promise<ToolErrorResult>;

  /**
   * @internal
   * True only for literal needsApproval: true. Dynamic approval functions own their input validation.
   */
  requiresApprovalValidation?: boolean;

  /**
   * 该工具在调用之前是否需要人工审批。若为 true，运行将产生一个 `interruption`，程序必须通过批准或拒绝该工具调用来解决。
   */
  needsApproval: ToolApprovalFunction<TParameters>;

  timeoutMs?: number;

  /**
   * 定义超时错误的处理方式。
   *
   * - `error_as_result`：返回一条模型可见的超时消息。
   * - `raise_exception`：抛出 `ToolTimeoutError` 并使运行失败。
   */
  timeoutBehavior?: FunctionToolTimeoutBehavior;

  /**
   * 当 timeoutBehavior 为 `error_as_result` 时，用于格式化超时错误的可选函数。
   */
  timeoutErrorFunction?: ToolTimeoutErrorFunction<Context>;

  /**
   * 决定该工具是否应在当前运行中对模型可用。
   */
  isEnabled: ToolEnabledFunction<Context>;
  /**
   * 在工具执行之前运行的护栏规则。
   */
  inputGuardrails?: ToolInputGuardrailDefinition<Context>[];
  /**
   * 在工具执行之后运行的护栏规则。
   */
  outputGuardrails?: ToolOutputGuardrailDefinition<Context>[];
};

export type FunctionToolExecutionOptions = {
  /**
   * `serial` tools with the same `concurrencyKey` run in model output order.
   * Tools without execution options continue to run in parallel.
   */
  mode: 'serial';
  concurrencyKey: string;
};

/**
 * 可由模型调用的工具。
 * @template Context 传递给工具的上下文
 */
export type Tool<Context = unknown> =
  | FunctionTool<Context, any, any>
  | HostedTool;

export type FunctionToolResult<
  Context = UnknownContext,
  TParameters extends ToolInputParameters = any,
  Result = any,
> =
  | {
      type: 'function_output';
      /**
       * 被调用的工具。
       */
      tool: FunctionTool<Context, TParameters, Result>;
      /**
       * 工具调用的输出。可以是字符串或可字符串化的内容。
       */
      output: string | unknown;
      /**
       * 表示工具调用输出的运行项。
       */
      runItem: RunToolCallOutputItem;
      /**
       * 工具执行中运行另一个代理时返回的结果。
       * 当调用源自 {@link Agent.asTool} 且嵌套代理完成了一次运行时，此字段会被填充。
       */
      agentRunResult?: RunResult<Context, Agent<Context, any>>;
      /**
       * 嵌套代理执行期间收集的所有中断。这些中断被暴露出来，
       * 以便调用者可以暂停和恢复需要审批的工作流。
       */
      interruptions?: RunToolApprovalItem[];
    }
  | {
      /**
       * 表示该工具在调用前需要获得审批。
       */
      type: 'function_approval';
      /**
       * 需要审批的工具。
       */
      tool: FunctionTool<Context, TParameters, Result>;
      /**
       * 表示需要审批的工具调用的运行项。
       */
      runItem: RunToolApprovalItem;
    }
  | {
      /**
       * Indicates that the tool requires approval before it can be called.
       */
      type: 'hosted_mcp_tool_approval';
      /**
       * The tool that is requiring to be approved.
       */
      tool: HostedMCPTool<Context>;
      /**
       * The item representing the tool call that is requiring approval.
       */
      runItem: RunToolApprovalItem;
    };

export type ToolEnabledFunction<Context = UnknownContext> = (
  runContext: RunContext<Context>,
  agent: Agent<any, any>,
) => Promise<boolean>;

export async function disposeResolvedComputers<Context>({
  runContext,
}: {
  runContext: RunContext<Context>;
}): Promise<void> {
  void runContext;
  //noop
}

/**
 * A built-in hosted tool that will be executed directly by the model during the request and won't result in local code executions.
 * Examples of these are `web_search_call` or `file_search_call`.
 *
 * @param Context The context of the tool
 * @param Result The result of the tool
 */
export type HostedTool = {
  type: 'hosted_tool';
  /**
   * A unique name for the tool.
   */
  name: string;
  /**
   * Additional configuration data that gets passed to the tool
   */
  providerData?: Record<string, any>;
};

/**
 * A hosted MCP tool that lets the model call a remote MCP server directly
 * without a round trip back to your code.
 */
export type HostedMCPTool<Context = UnknownContext> = HostedTool & {
  name: 'hosted_mcp';
  providerData: ProviderData.HostedMCPTool<Context>;
};

export type HostedMCPApprovalFunction<Context = UnknownContext> = (
  context: RunContext<Context>,
  data: RunToolApprovalItem,
) => Promise<{ approve: boolean; reason?: string }>;

type FunctionToolInvocationArgs<
  Context = UnknownContext,
  TParameters extends ToolInputParameters = ToolInputParameters,
  Result = unknown,
> = {
  tool: FunctionTool<Context, TParameters, Result>;
  runContext: RunContext<Context>;
  input: string;
  details?: ToolCallDetails;
};

type FunctionToolInvokeFunction<Context = UnknownContext, Result = unknown> = (
  runContext: RunContext<Context>,
  input: string,
  details?: ToolCallDetails,
) => Promise<string | Result | ToolErrorResult>;
type ToolCallDetailsWithTimeoutFlag = ToolCallDetails & {
  [FUNCTION_TOOL_TIMEOUT_ALREADY_ENFORCED]?: true;
};
const FUNCTION_TOOL_WRAP_ERROR_RESULT = Symbol('functionToolWrapErrorResult');
type ToolCallDetailsWithErrorResultFlag = ToolCallDetails & {
  [FUNCTION_TOOL_WRAP_ERROR_RESULT]?: true;
};

/**
 * Invoke a function tool while enforcing optional per-tool timeout settings.
 */
export async function invokeFunctionTool<
  Context = UnknownContext,
  TParameters extends ToolInputParameters = ToolInputParameters,
  Result = unknown,
>(
  args: FunctionToolInvocationArgs<Context, TParameters, Result>,
): Promise<string | Result | ToolErrorResult> {
  const { tool, runContext, input, details } = args;
  const invokeWithTimeoutFlag: FunctionToolInvokeFunction<Context, Result> = (
    invocationRunContext,
    invocationInput,
    invocationDetails,
  ) => {
    const detailsWithFlag: ToolCallDetailsWithTimeoutFlag &
      ToolCallDetailsWithErrorResultFlag =
      typeof invocationDetails === 'undefined'
        ? ({
            [FUNCTION_TOOL_TIMEOUT_ALREADY_ENFORCED]: true as const,
            [FUNCTION_TOOL_WRAP_ERROR_RESULT]: true as const,
          } as ToolCallDetailsWithTimeoutFlag &
            ToolCallDetailsWithErrorResultFlag)
        : cloneObjectWithDescriptorsAndOverrides(invocationDetails, {
            [FUNCTION_TOOL_TIMEOUT_ALREADY_ENFORCED]: true as const,
            [FUNCTION_TOOL_WRAP_ERROR_RESULT]: true as const,
          });

    return tool.invoke(invocationRunContext, invocationInput, detailsWithFlag);
  };

  return invokeFunctionToolWithTimeout<Context, Result>({
    toolName: tool.name,
    invoke: invokeWithTimeoutFlag,
    runContext,
    input,
    details,
    timeoutMs: tool.timeoutMs,
    timeoutBehavior: tool.timeoutBehavior,
    timeoutErrorFunction: tool.timeoutErrorFunction,
  });
}

function cloneObjectWithDescriptorsAndOverrides<
  TBase extends object,
  TOverrides extends object,
>(value: TBase, overrides: TOverrides): TBase & TOverrides {
  const descriptors = Object.getOwnPropertyDescriptors(value) as Record<
    PropertyKey,
    PropertyDescriptor
  >;
  for (const key of Reflect.ownKeys(overrides)) {
    delete descriptors[key];
  }
  const clone = Object.create(
    Object.getPrototypeOf(value),
    descriptors,
  ) as TBase & TOverrides;
  return Object.assign(clone, overrides);
}

async function invokeFunctionToolWithTimeout<
  Context = UnknownContext,
  Result = unknown,
>(args: {
  toolName: string;
  invoke: FunctionToolInvokeFunction<Context, Result>;
  runContext: RunContext<Context>;
  input: string;
  details?: ToolCallDetails;
  timeoutMs?: number;
  timeoutBehavior?: FunctionToolTimeoutBehavior;
  timeoutErrorFunction?: ToolTimeoutErrorFunction<Context>;
}): Promise<string | Result | ToolErrorResult> {
  const {
    toolName,
    invoke,
    runContext,
    input,
    details,
    timeoutMs: configuredTimeoutMs,
    timeoutBehavior: configuredTimeoutBehavior,
    timeoutErrorFunction: configuredTimeoutErrorFunction,
  } = args;
  const { timeoutMs, timeoutBehavior, timeoutErrorFunction } =
    normalizeFunctionToolTimeoutConfig({
      toolName,
      timeoutMs: configuredTimeoutMs,
      timeoutBehavior: configuredTimeoutBehavior,
      timeoutErrorFunction: configuredTimeoutErrorFunction,
    });

  if (typeof timeoutMs === 'undefined') {
    return invoke(runContext, input, details);
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let timeoutTriggered = false;
  const timeoutController = new AbortController();
  const { signal: invocationSignal, cleanup: cleanupAbortSignals } =
    combineAbortSignals(details?.signal, timeoutController.signal);
  const invokeDetails: ToolCallDetails | undefined = invocationSignal
    ? details
      ? cloneObjectWithDescriptorsAndOverrides(details, {
          signal: invocationSignal,
        })
      : { signal: invocationSignal }
    : details;
  const timeoutError = new ToolTimeoutError({
    toolName,
    timeoutMs,
  });

  try {
    return await Promise.race([
      invoke(runContext, input, invokeDetails),
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => {
          timeoutTriggered = true;
          reject(timeoutError);
          timeoutController.abort(timeoutError);
        }, timeoutMs);
      }),
    ]);
  } catch (error) {
    const isTimeoutError =
      error === timeoutError ||
      (timeoutTriggered && timeoutController.signal.reason === timeoutError);
    if (!isTimeoutError) {
      throw error;
    }

    if (timeoutBehavior === 'raise_exception') {
      throw timeoutError;
    }

    if (timeoutErrorFunction) {
      return timeoutErrorFunction(runContext, timeoutError);
    }

    return defaultFunctionToolTimeoutErrorMessage({
      toolName,
      timeoutMs,
    });
  } finally {
    if (typeof timeoutId !== 'undefined') {
      clearTimeout(timeoutId);
    }
    cleanupAbortSignals();
  }
}

function normalizeFunctionToolTimeoutConfig<Context = UnknownContext>(args: {
  toolName: string;
  timeoutMs?: number;
  timeoutBehavior?: FunctionToolTimeoutBehavior;
  timeoutErrorFunction?: ToolTimeoutErrorFunction<Context>;
}): {
  timeoutMs?: number;
  timeoutBehavior: FunctionToolTimeoutBehavior;
  timeoutErrorFunction?: ToolTimeoutErrorFunction<Context>;
} {
  const { toolName, timeoutMs, timeoutBehavior, timeoutErrorFunction } = args;

  if (typeof timeoutMs !== 'undefined') {
    if (typeof timeoutMs !== 'number' || Number.isNaN(timeoutMs)) {
      throw new UserError(
        `Function tool '${toolName}' timeoutMs must be a finite number in milliseconds.`,
      );
    }
    if (!Number.isFinite(timeoutMs)) {
      throw new UserError(
        `Function tool '${toolName}' timeoutMs must be finite.`,
      );
    }
    if (timeoutMs <= 0) {
      throw new UserError(
        `Function tool '${toolName}' timeoutMs must be greater than 0.`,
      );
    }
    if (timeoutMs > MAX_FUNCTION_TOOL_TIMEOUT_MS) {
      throw new UserError(
        `Function tool '${toolName}' timeoutMs must be less than or equal to ${MAX_FUNCTION_TOOL_TIMEOUT_MS}.`,
      );
    }
  }

  const resolvedBehavior = timeoutBehavior ?? 'error_as_result';
  if (!FUNCTION_TOOL_TIMEOUT_BEHAVIORS.includes(resolvedBehavior)) {
    throw new UserError(
      `Function tool '${toolName}' timeoutBehavior must be one of: ${FUNCTION_TOOL_TIMEOUT_BEHAVIORS.join(', ')}.`,
    );
  }

  if (
    typeof timeoutErrorFunction !== 'undefined' &&
    typeof timeoutErrorFunction !== 'function'
  ) {
    throw new UserError(
      `Function tool '${toolName}' timeoutErrorFunction must be a function when provided.`,
    );
  }

  return {
    timeoutMs,
    timeoutBehavior: resolvedBehavior,
    timeoutErrorFunction,
  };
}

function defaultFunctionToolTimeoutErrorMessage(args: {
  toolName: string;
  timeoutMs: number;
}): string {
  return `Tool '${args.toolName}' timed out after ${args.timeoutMs}ms.`;
}

export type ClientToolSearchLoadDefaultFunction<Context = UnknownContext> = (
  paths: string[],
) => Tool<Context>[];

export type ClientToolSearchExecutorArgs<Context = UnknownContext> = {
  agent: Agent<Context, any>;
  availableTools: Tool<Context>[];
  loadDefault: ClientToolSearchLoadDefaultFunction<Context>;
  runContext: RunContext<Context>;
};

export type ClientToolSearchExecutorResult<Context = UnknownContext> =
  | Tool<Context>
  | Tool<Context>[]
  | null
  | undefined;

export type ClientToolSearchExecutor<Context = UnknownContext> = (
  args: ClientToolSearchExecutorArgs<Context>,
) =>
  | ClientToolSearchExecutorResult<Context>
  | Promise<ClientToolSearchExecutorResult<Context>>;

export function getClientToolSearchExecutor<Context = UnknownContext>(
  tool: Tool<Context>,
): ClientToolSearchExecutor<Context> | undefined {
  void tool;
  return undefined;
}

/**
 * The parameters of a tool that has strict mode enabled.
 *
 * This can be a Zod schema, a JSON schema or undefined.
 *
 * If a Zod schema is provided, the arguments to the tool will automatically be parsed and validated
 * against the schema.
 *
 * If a JSON schema is provided, the arguments to the tool will be parsed as JSON but not validated.
 *
 * If undefined is provided, the arguments to the tool will be passed as a string.
 */
export type ToolInputParametersStrict =
  | undefined
  | ZodObjectLike
  | JsonObjectSchemaStrict<any>;

/**
 * The parameters of a tool that has strict mode disabled.
 *
 * If a JSON schema is provided, the arguments to the tool will be parsed as JSON but not validated.
 *
 * Zod schemas are not supported without strict: true.
 */
export type ToolInputParametersNonStrict =
  | undefined
  | JsonObjectSchemaNonStrict<any>;

type ToolGuardrailOptions<Context = UnknownContext> = {
  /**
   * Guardrails that validate or block tool invocation before it runs.
   */
  inputGuardrails?:
    | ToolInputGuardrailDefinition<Context>[]
    | {
        name: string;
        run: ToolInputGuardrailFunction<Context>;
      }[];
  /**
   * Guardrails that validate or alter tool output after it runs.
   */
  outputGuardrails?:
    | ToolOutputGuardrailDefinition<Context>[]
    | {
        name: string;
        run: ToolOutputGuardrailFunction<Context>;
      }[];
};

/**
 * The function to invoke when the tool is called.
 *
 * @param input The arguments to the tool (see ToolExecuteArgument)
 * @param context An instance of the current RunContext
 */
type ToolExecuteFunction<
  TParameters extends ToolInputParameters,
  Context = UnknownContext,
> = (
  input: ToolExecuteArgument<TParameters>,
  context?: RunContext<Context>,
  details?: ToolCallDetails,
) => Promise<unknown> | unknown;

type ToolEnabledPredicate<Context = UnknownContext> = (args: {
  runContext: RunContext<Context>;
  agent: Agent<any, any>;
}) => boolean | Promise<boolean>;

type ToolEnabledOption<Context = UnknownContext> =
  | boolean
  | ToolEnabledPredicate<Context>;

/**
 * The function to invoke when an error occurs while running the tool. This can be used to define
 * what the model should receive as tool output in case of an error. It can be used to provide
 * for example additional context or a fallback value.
 *
 * @param context An instance of the current RunContext
 * @param error The error that occurred
 */
type ToolErrorFunction = (
  context: RunContext,
  error: Error | unknown,
) => Promise<string> | string;

/**
 * The options for a tool that has strict mode enabled.
 *
 * @param TParameters The parameters of the tool
 * @param Context The context of the tool
 */
type StrictToolOptions<
  TParameters extends ToolInputParametersStrict,
  Context = UnknownContext,
> = ToolGuardrailOptions<Context> & {
  /**
   * The name of the tool. Must be unique within the agent.
   */
  name?: string;

  /**
   * The description of the tool. This is used to help the model understand when to use the tool.
   */
  description: string;

  /**
   * Controls how this tool is scheduled relative to other calls from the same model turn.
   */
  execution?: FunctionToolExecutionOptions;

  /**
   * A Zod schema or JSON schema describing the parameters of the tool.
   * If a Zod schema is provided, the arguments to the tool will automatically be parsed and validated
   * against the schema.
   */
  parameters: TParameters;

  /**
   * Whether the tool is strict. If true, the model must try to strictly follow the schema (might result in slower response times).
   */
  strict?: true;

  /**
   * The function to invoke when the tool is called.
   */
  execute: ToolExecuteFunction<TParameters, Context>;

  /**
   * The function to invoke when an error occurs while running the tool.
   */
  errorFunction?: ToolErrorFunction | null;

  /**
   * Whether the tool needs human approval before it can be called. If this is true, the run will result in an `interruption` that the
   * program has to resolve by approving or rejecting the tool call.
   */
  needsApproval?: boolean | ToolApprovalFunction<TParameters>;

  /**
   * Determines whether the tool should be exposed to the model for the current run.
   */
  isEnabled?: ToolEnabledOption<Context>;

  /**
   * Optional timeout in milliseconds for each tool call.
   */
  timeoutMs?: number;

  /**
   * Timeout handling mode. `error_as_result` returns a model-visible message and
   * `raise_exception` throws `ToolTimeoutError`.
   */
  timeoutBehavior?: FunctionToolTimeoutBehavior;

  /**
   * Optional formatter used for timeout messages when timeoutBehavior is `error_as_result`.
   */
  timeoutErrorFunction?: ToolTimeoutErrorFunction<Context>;
};

/**
 * The options for a tool that has strict mode disabled.
 *
 * @param TParameters The parameters of the tool
 * @param Context The context of the tool
 */
type NonStrictToolOptions<
  TParameters extends ToolInputParametersNonStrict,
  Context = UnknownContext,
> = ToolGuardrailOptions<Context> & {
  /**
   * The name of the tool. Must be unique within the agent.
   */
  name?: string;

  /**
   * The description of the tool. This is used to help the model understand when to use the tool.
   */
  description: string;

  /**
   * Controls how this tool is scheduled relative to other calls from the same model turn.
   */
  execution?: FunctionToolExecutionOptions;

  /**
   * A JSON schema of the tool. To use a Zod schema, you need to use a `strict` schema.
   */
  parameters: TParameters;

  /**
   * Whether the tool is strict  If true, the model must try to strictly follow the schema (might result in slower response times).
   */
  strict: false;

  /**
   * The function to invoke when the tool is called.
   */
  execute: ToolExecuteFunction<TParameters, Context>;

  /**
   * The function to invoke when an error occurs while running the tool.
   */
  errorFunction?: ToolErrorFunction | null;

  /**
   * Whether the tool needs human approval before it can be called. If this is true, the run will result in an `interruption` that the
   * program has to resolve by approving or rejecting the tool call.
   */
  needsApproval?: boolean | ToolApprovalFunction<TParameters>;

  /**
   * Determines whether the tool should be exposed to the model for the current run.
   */
  isEnabled?: ToolEnabledOption<Context>;

  /**
   * Optional timeout in milliseconds for each tool call.
   */
  timeoutMs?: number;

  /**
   * Timeout handling mode. `error_as_result` returns a model-visible message and
   * `raise_exception` throws `ToolTimeoutError`.
   */
  timeoutBehavior?: FunctionToolTimeoutBehavior;

  /**
   * Optional formatter used for timeout messages when timeoutBehavior is `error_as_result`.
   */
  timeoutErrorFunction?: ToolTimeoutErrorFunction<Context>;
};

/**
 * The options for a tool.
 *
 * @param TParameters The parameters of the tool
 * @param Context The context of the tool
 */
export type ToolOptions<
  TParameters extends ToolInputParameters,
  Context = UnknownContext,
> =
  | StrictToolOptions<Extract<TParameters, ToolInputParametersStrict>, Context>
  | NonStrictToolOptions<
      Extract<TParameters, ToolInputParametersNonStrict>,
      Context
    >;

/**
 * Exposes a function to the agent as a tool to be called
 *
 * @param options The options for the tool
 * @returns A new tool
 */
export function tool<
  TParameters extends ToolInputParameters = undefined,
  Context = UnknownContext,
  Result = string,
>(
  options: ToolOptions<TParameters, Context>,
): FunctionTool<Context, TParameters, Result> {
  const name = options.name
    ? toFunctionToolName(options.name)
    : toFunctionToolName(options.execute.name);
  const toolErrorFunction: ToolErrorFunction | null =
    typeof options.errorFunction === 'undefined'
      ? defaultToolErrorFunction
      : options.errorFunction;

  if (!name) {
    throw new Error(
      'Tool name cannot be empty. Either name your function or provide a name in the options.',
    );
  }
  const strictMode = options.strict ?? true;
  if (!strictMode && isZodObject(options.parameters)) {
    throw new UserError('Strict mode is required for Zod parameters');
  }
  const { timeoutMs, timeoutBehavior, timeoutErrorFunction } =
    normalizeFunctionToolTimeoutConfig({
      toolName: name,
      timeoutMs: options.timeoutMs,
      timeoutBehavior: options.timeoutBehavior,
      timeoutErrorFunction: options.timeoutErrorFunction,
    });

  const { parser, schema: parameters } = getSchemaAndParserFromInputType(
    options.parameters,
    name,
    {
      strict: strictMode,
    },
  );

  async function parseInput(
    runContext: RunContext<Context>,
    input: string,
    details?: ToolCallDetails,
  ): Promise<ToolExecuteArgument<TParameters>> {
    const [error, parsed] = await safeExecute(() => parser(input));
    if (error !== null) {
      if (logger.dontLogToolData) {
        logger.debug(`Invalid JSON input for tool ${name}`);
      } else {
        logger.debug(`Invalid JSON input for tool ${name}: ${input}`);
      }

      // supply the same context as options.execute for consuming
      // downstream code to implement self-healing and/or tracing
      throw new InvalidToolInputError(
        'Invalid JSON input for tool',
        undefined, // no RunState available in this context
        error,
        { runContext, input, details },
      );
    }

    return parsed as ToolExecuteArgument<TParameters>;
  }

  async function _invoke(
    runContext: RunContext<Context>,
    input: string,
    details?: ToolCallDetails,
  ): Promise<Result> {
    const parsed = await parseInput(runContext, input, details);

    if (logger.dontLogToolData) {
      logger.debug(`Invoking tool ${name}`);
    } else {
      logger.debug(`Invoking tool ${name} with input ${input}`);
    }

    const result = await options.execute(parsed, runContext, details);
    const stringResult = toSmartString(result);

    if (logger.dontLogToolData) {
      logger.debug(`Tool ${name} completed`);
    } else {
      logger.debug(`Tool ${name} returned: ${stringResult}`);
    }

    return result as Result;
  }

  function recordToolError(error: unknown): void {
    const currentSpan = getCurrentSpan();
    currentSpan?.setError({
      message: 'Error running tool (non-fatal)',
      data: {
        tool_name: name,
        error: error instanceof Error ? error.toString() : String(error),
      },
    });
  }

  async function handleError(
    runContext: RunContext<Context>,
    error: unknown,
    _details?: ToolCallDetails,
  ): Promise<ToolErrorResult> {
    if (!toolErrorFunction) {
      throw error;
    }

    recordToolError(error);
    const formatted = await toolErrorFunction(runContext, error);
    const resolved = resolveToolResult(formatted);
    return toolError(resolved.output, { display: resolved.display });
  }

  async function invokeWithoutTimeout(
    runContext: RunContext<Context>,
    input: string,
    details?: ToolCallDetails,
  ): Promise<string | Result | ToolErrorResult> {
    try {
      return await _invoke(runContext, input, details);
    } catch (error) {
      if (
        details?.signal?.aborted &&
        details.signal.reason instanceof ToolTimeoutError
      ) {
        throw error;
      }

      if (toolErrorFunction) {
        if (
          !(details as ToolCallDetailsWithErrorResultFlag | undefined)?.[
            FUNCTION_TOOL_WRAP_ERROR_RESULT
          ]
        ) {
          recordToolError(error);
          return toolErrorFunction(runContext, error);
        }
        return handleError(runContext, error, details);
      }

      throw error;
    }
  }

  async function invoke(
    runContext: RunContext<Context>,
    input: string,
    details?: ToolCallDetails,
  ): Promise<string | Result | ToolErrorResult> {
    const detailsWithFlag = details as
      | ToolCallDetailsWithTimeoutFlag
      | undefined;
    if (detailsWithFlag?.[FUNCTION_TOOL_TIMEOUT_ALREADY_ENFORCED]) {
      return invokeWithoutTimeout(runContext, input, details);
    }

    return invokeFunctionToolWithTimeout<Context, Result>({
      toolName: name,
      invoke: invokeWithoutTimeout,
      runContext,
      input,
      details,
      timeoutMs,
      timeoutBehavior,
      timeoutErrorFunction,
    });
  }

  const needsApproval: ToolApprovalFunction<TParameters> =
    typeof options.needsApproval === 'function'
      ? options.needsApproval
      : async () =>
          typeof options.needsApproval === 'boolean'
            ? options.needsApproval
            : false;

  const isEnabled: ToolEnabledFunction<Context> =
    typeof options.isEnabled === 'function'
      ? async (runContext, agent) => {
          const predicate = options.isEnabled as ToolEnabledPredicate<Context>;
          const result = await predicate({ runContext, agent });
          return Boolean(result);
        }
      : async () =>
          typeof options.isEnabled === 'boolean' ? options.isEnabled : true;

  return {
    type: 'function',
    name,
    description: options.description,
    ...(options.execution ? { execution: options.execution } : {}),
    parameters,
    strict: strictMode,
    invoke,
    parseInput,
    handleError,
    requiresApprovalValidation: options.needsApproval === true,
    needsApproval,
    timeoutMs,
    timeoutBehavior,
    timeoutErrorFunction,
    isEnabled,
    inputGuardrails: resolveToolInputGuardrails(options.inputGuardrails),
    outputGuardrails: resolveToolOutputGuardrails(options.outputGuardrails),
  };
}

/**
 * The default function to invoke when an error occurs while running the tool.
 *
 * Always returns `An error occurred while running the tool. Please try again. Error: <error details>`
 *
 * @param context An instance of the current RunContext
 * @param error The error that occurred
 */
function defaultToolErrorFunction(
  _context: RunContext,
  error: Error | unknown,
) {
  if (error instanceof InvalidToolInputError) {
    return formatInvalidToolInputError(error);
  }

  const details = error instanceof Error ? error.toString() : String(error);
  return `An error occurred while running the tool. Please try again. Error: ${details}`;
}

type ZodIssueLike = {
  path?: unknown[];
  message?: string;
};

const MISSING_VALUE = Symbol('missingToolInputValue');

function formatInvalidToolInputError(error: InvalidToolInputError): string {
  const toolName = error.toolInvocation?.details?.toolCall?.name;
  const toolNameSuffix = toolName ? ` for ${toolName}` : '';
  const issues = getZodIssues(error.originalError);

  if (issues) {
    const parsedInput = parseToolInvocationInput(error.toolInvocation?.input);
    const issueLines = issues
      .map((issue) => {
        const path = formatZodIssuePath(issue.path);
        const message = issue.message ?? 'Invalid value';
        return `- ${path}: ${message}${formatReceivedValue(parsedInput, issue.path)}`;
      })
      .join('\n');

    return `Tool input validation failed${toolNameSuffix}:\n${issueLines}\n\nPlease retry with valid arguments.`;
  }

  const details =
    error.originalError instanceof Error
      ? error.originalError.message
      : String(error.originalError ?? error);
  return `Tool input must be valid JSON${toolNameSuffix}: ${details}\n\nPlease retry with a valid JSON object.`;
}

function getZodIssues(error: unknown): ZodIssueLike[] | undefined {
  if (!isPlainObject(error)) return undefined;
  const issues = error.issues;
  if (!Array.isArray(issues)) return undefined;

  const validIssues = issues.filter(
    (issue): issue is ZodIssueLike =>
      isPlainObject(issue) && typeof issue.message === 'string',
  );
  return validIssues.length > 0 ? validIssues : undefined;
}

function formatZodIssuePath(path: unknown): string {
  if (!Array.isArray(path) || path.length === 0) return '<root>';

  let formatted = '';
  for (const segment of path) {
    if (typeof segment === 'number') {
      formatted += `[${segment}]`;
      continue;
    }

    const key = String(segment);
    formatted += formatted ? `.${key}` : key;
  }

  return formatted || '<root>';
}

function parseToolInvocationInput(input: string | undefined): unknown {
  if (typeof input !== 'string') return MISSING_VALUE;
  try {
    return JSON.parse(input) as unknown;
  } catch {
    return MISSING_VALUE;
  }
}

function formatReceivedValue(parsedInput: unknown, path: unknown): string {
  if (parsedInput === MISSING_VALUE || !Array.isArray(path)) return '';

  const value = readValueAtPath(parsedInput, path);
  if (value === MISSING_VALUE) return ' (received: missing)';

  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return ` (received: ${JSON.stringify(value)})`;
  }

  if (Array.isArray(value)) return ' (received: array)';
  if (isPlainObject(value)) return ' (received: object)';
  return '';
}

function readValueAtPath(value: unknown, path: unknown[]): unknown {
  let current = value;

  for (const segment of path) {
    if (typeof segment === 'number') {
      if (!Array.isArray(current) || segment < 0 || segment >= current.length)
        return MISSING_VALUE;
      current = current[segment];
      continue;
    }

    if (!isPlainObject(current) || !Object.hasOwn(current, String(segment))) {
      return MISSING_VALUE;
    }
    current = current[String(segment)];
  }

  return current;
}
